/**
 * Coordinate parsing for the go-to box (PRD P1.3). Supported forms:
 *   45.5017, -73.5673          decimal lat, lon (comma/semicolon/space)
 *   45.5017 N 73.5673 W        decimal with hemisphere letters
 *   45°30'07"N 73°34'02"W      DMS with °/'/" symbols (seconds optional)
 * Returns null for anything else — the UI shows a gentle hint, never throws.
 */
export interface LatLon {
  lat: number
  lon: number
}

function validated(lat: number, lon: number): LatLon | null {
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    ? { lat, lon }
    : null
}

const DMS_PART = /(\d+(?:\.\d+)?)\s*°\s*(?:(\d+(?:\.\d+)?)\s*['’]\s*)?(?:(\d+(?:\.\d+)?)\s*["”]\s*)?([NSEW])/gi
const DEC_HEMI = /^(\d+(?:\.\d+)?)\s*([NS])[,;\s]+(\d+(?:\.\d+)?)\s*([EW])$/i

export function parseCoordinate(input: string): LatLon | null {
  const s = input.trim()
  if (!s) return null

  const decHemi = DEC_HEMI.exec(s)
  if (decHemi) {
    const lat = Number(decHemi[1]) * (decHemi[2].toUpperCase() === 'S' ? -1 : 1)
    const lon = Number(decHemi[3]) * (decHemi[4].toUpperCase() === 'W' ? -1 : 1)
    return validated(lat, lon)
  }

  if (s.includes('°')) {
    const parts = [...s.matchAll(DMS_PART)]
    if (parts.length !== 2) return null
    let lat: number | null = null
    let lon: number | null = null
    for (const m of parts) {
      const deg = Number(m[1]) + Number(m[2] ?? 0) / 60 + Number(m[3] ?? 0) / 3600
      const hemi = m[4].toUpperCase()
      const signed = hemi === 'S' || hemi === 'W' ? -deg : deg
      if (hemi === 'N' || hemi === 'S') lat = signed
      else lon = signed
    }
    if (lat === null || lon === null) return null
    return validated(lat, lon)
  }

  const numbers = s.split(/[,;\s]+/).map(Number)
  if (numbers.length === 2 && numbers.every(Number.isFinite)) {
    return validated(numbers[0], numbers[1])
  }
  return null
}
